import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Clapperboard, ExternalLink, FileVideo, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtShort, fmtMin } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, PageHeader, Spinner } from "@/components/shared";
import LiveWall from "@/components/LiveWall";
import { cn } from "@/lib/utils";

const CHANNEL_OPTS = [["cabin", "Cabin"], ["road", "Road"], ["both", "Both"]];

/** Remote-capture controls for a real camera device (the live view itself is the native LiveWall). */
function CaptureBar({ s }: { s: any }) {
  const [channel, setChannel] = useState("cabin");
  const [seconds, setSeconds] = useState(5);
  const capture = useMutation({
    mutationFn: () => api.post("/cctv/capture", { vehicleId: s.vehicleId, channel, seconds, video: true }),
    onSuccess: (r: any) => toast.success(`Capture ordered · ${channel} · ${seconds}s · cmd ${r.data?.cmdSeqNo || "—"}`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div data-testid={`cctv-capture-bar-${s.plate}`} className="tg-card flex flex-wrap items-center gap-2 px-3 py-2 mb-4">
      <span className="font-mono text-xs text-slate-100">{s.plate}</span>
      <span className="font-mono text-[10px] text-slate-500">· {s.provider} · capture</span>
      <div className="flex rounded-md border border-[#26334d] overflow-hidden" data-testid={`cctv-channel-${s.plate}`}>
        {CHANNEL_OPTS.map(([v, l]) => (
          <button key={v} onClick={() => setChannel(v)} data-testid={`cctv-channel-${s.plate}-${v}`}
                  className={cn("font-mono text-[10px] px-2.5 py-1", channel === v ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-200")}>{l}</button>
        ))}
      </div>
      <div className="flex rounded-md border border-[#26334d] overflow-hidden" data-testid={`cctv-seconds-${s.plate}`}>
        {[3, 5, 8, 10].map((n) => (
          <button key={n} onClick={() => setSeconds(n)} data-testid={`cctv-seconds-${s.plate}-${n}`}
                  className={cn("font-mono text-[10px] px-2 py-1", seconds === n ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-200")}>{n}s</button>
        ))}
      </div>
      <Button size="sm" variant="secondary" data-testid={`cctv-capture-${s.plate}`} disabled={capture.isPending} onClick={() => capture.mutate()} className="h-7 text-[11px]">
        <Clapperboard className="h-3 w-3 mr-1" />{capture.isPending ? "Sending…" : "Capture now"}
      </Button>
    </div>
  );
}

function LiveTile({ s }: { s: any }) {
  const [tick, setTick] = useState(0);
  // Refresh only while the operator is actually looking. Each tile is a fresh frame fetch, and on
  // a real device that frame comes over the vehicle's SIM — a wall left open in a background tab
  // would spend a fleet's data allowance on pictures nobody sees.
  useEffect(() => {
    let timer: any;
    const start = () => { stop(); timer = setInterval(() => setTick((x) => x + 1), 5000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = undefined; } };
    const onVisibility = () => (document.visibilityState === "visible" ? start() : stop());
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);
  return (
    <div data-testid={`cctv-tile-${s.plate}-${s.channel}`} className="tg-card overflow-hidden">
      <img src={`${BACKEND_URL}${s.frameUrl}&t=${tick}`} alt={`${s.plate} ${s.channel}`} className="w-full aspect-video object-cover bg-[#020617]" />
      <div className="flex items-center justify-between px-3 py-2 text-xs">
        <span className="font-mono text-slate-100">{s.plate} <span className="text-slate-500">· {s.label}</span></span>
        <span className={cn("font-mono text-[10px]", s.status === "PENDING_HARDWARE_TEST" ? "text-amber-400" : "text-emerald-400")}>{s.status.replace(/_/g, " ")}</span>
      </div>
    </div>
  );
}

export function useRequestClip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => api.post("/cctv/clips", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clips"] }); qc.invalidateQueries({ queryKey: ["cctv-history"] }); qc.invalidateQueries({ queryKey: ["rec"] }); toast.success("Evidence clip requested (mock provider: READY)"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
}

export async function openClip(id: string) {
  try {
    const { data } = await api.get(`/cctv/clips/${id}/url`);
    // A real upload lives on the camera platform, so its URL is absolute; the placeholder is a
    // signed path on our own API. Prefixing the backend URL to both sent the real one nowhere.
    window.open(data.external ? data.url : `${BACKEND_URL}${data.url}`, "_blank", "noopener");
    if (data.external) toast.success("Opening the recording · access logged");
    else toast.warning(data.detail || "No footage for this clip yet — opening the placeholder");
  } catch (e) { toast.error(errorMessage(e)); }
}

export default function CCTV() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const streams = useQuery({ queryKey: ["cctv-streams"], queryFn: async () => (await api.get("/cctv/streams")).data, refetchInterval: 600000 });
  const events = useQuery({ queryKey: ["cctv-events"], queryFn: async () => (await api.get("/cctv/events", { params: { limit: 150 } })).data, refetchInterval: 30000 });
  const clips = useQuery({ queryKey: ["clips"], queryFn: async () => (await api.get("/cctv/clips")).data });
  const vehicles = useQuery({ queryKey: ["vehicles"], queryFn: async () => (await api.get("/vehicles")).data });
  const [vehicleId, setVehicleId] = useState("");
  const [channel, setChannel] = useState("cabin");
  // Any moment, not just a whole journey: an incident rarely lines up with one.
  const [at, setAt] = useState("");
  const [minutes, setMinutes] = useState(3);
  const history = useQuery({ queryKey: ["cctv-history", vehicleId], queryFn: async () => (await api.get("/cctv/history", { params: { vehicleId } })).data, enabled: !!vehicleId });
  const request = useRequestClip();
  const media = useQuery({
    queryKey: ["cctv-media", vehicleId],
    queryFn: async () => (await api.get("/cctv/media", { params: { vehicleId, hours: 168 } })).data,
    enabled: !!vehicleId,
  });
  const checkUpload = useMutation({
    mutationFn: (id: string) => api.post(`/cctv/clips/${id}/check`),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["clips"] });
      const c = r.data;
      if (c.fileUrl) toast.success("The device uploaded the fragment — the clip now has real footage");
      else toast.info(c.lookupError || c.detail || "Nothing uploaded for that window yet");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const del = useMutation({ mutationFn: (id: string) => api.delete(`/cctv/clips/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["clips"] }); toast.success("Clip deleted"); } });
  useEffect(() => { if (!vehicleId && vehicles.data?.length) setVehicleId(vehicles.data[0].id); }, [vehicles.data, vehicleId]);

  return (
    <div data-testid="cctv-page">
      <PageHeader title="CCTV & evidence" subtitle={`CameraProvider = ${streams.data?.provider || "…"} · real devices stream live from TrackSolid; simulated vehicles stay mock frames · signed URLs · retention applied`} />
      <Tabs defaultValue="live">
        <TabsList className="bg-[#121824] border border-[#26334d]">
          {[["live", "LIVE"], ["events", "EVENTS"], ["history", "HISTORY"], ["media", "MEDIA"], ["evidence", "EVIDENCE"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`cctv-tab-${v}`} className="font-mono text-xs data-[state=active]:bg-blue-500/15 data-[state=active]:text-blue-300">{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="live" className="mt-4">
          {streams.isLoading ? <Spinner /> : (() => {
            const tiles = streams.data.streams.filter((s: any) => s.channel === "cabin");
            const real = tiles.filter((s: any) => s.liveUrl);
            const mock = tiles.filter((s: any) => !s.liveUrl);
            return (
              <>
                <LiveWall />
                {real.map((s: any) => <CaptureBar key={s.id} s={s} />)}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="cctv-live-grid">
                  {mock.map((s: any) => <LiveTile key={s.id} s={s} />)}
                </div>
              </>
            );
          })()}
        </TabsContent>
        <TabsContent value="events" className="mt-4">
          <div className="tg-card divide-y divide-[#26334d]" data-testid="cctv-events-list">
            {!events.data?.length && <EmptyState text="No camera events" />}
            {(events.data || []).map((e: any) => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                <Camera className="h-4 w-4 text-purple-400" />
                <span className="font-mono text-slate-100 w-24">{e.plate}</span>
                <span className="text-slate-200 flex-1">{e.attributes?.label?.replace(/_/g, " ")} <span className="text-slate-500">· {e.attributes?.camera} cam · analytics {e.attributes?.analytics}</span></span>
                <span className="font-mono text-slate-500">{fmtDateTime(e.timestamp)}</span>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <div className="flex gap-2 mb-3">
            <select data-testid="cctv-history-vehicle-select" className="h-9 rounded-md bg-[#0f141f] border border-[#26334d] text-sm px-3 text-slate-100" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>{(vehicles.data || []).map((v: any) => <option key={v.id} value={v.id}>{v.plate}</option>)}</select>
            <select data-testid="cctv-history-channel-select" className="h-9 rounded-md bg-[#0f141f] border border-[#26334d] text-sm px-3 text-slate-100" value={channel} onChange={(e) => setChannel(e.target.value)}><option value="cabin">cabin</option><option value="road">road</option></select>
          </div>
          {can("reconciliation:decide") && (
            <div className="tg-card p-4 mb-3 space-y-2" data-testid="cctv-window-request">
              <div className="tg-label">Ask for a specific moment</div>
              <p className="text-xs text-slate-400">
                The unit keeps its own recording. Give it a time and it uploads that fragment; it arrives
                when the vehicle next has signal, then appears under MEDIA and against the clip.
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <label className="block text-xs text-slate-400">Moment
                  <Input data-testid="cctv-window-at" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)}
                         className="mt-1 h-9 bg-[#0f141f] border-[#26334d] font-mono text-xs" /></label>
                <label className="block text-xs text-slate-400">Minutes
                  <Input data-testid="cctv-window-minutes" type="number" min={1} max={120} value={minutes}
                         onChange={(e) => setMinutes(Math.max(1, Math.min(120, Number(e.target.value) || 1)))}
                         className="mt-1 h-9 w-24 bg-[#0f141f] border-[#26334d] font-mono text-xs" /></label>
                <Button size="sm" data-testid="cctv-window-request-button" disabled={!at || !vehicleId || request.isPending}
                        onClick={() => {
                          const start = new Date(at);
                          request.mutate({ vehicleId, channel, startTime: start.toISOString(),
                                           endTime: new Date(start.getTime() + minutes * 60000).toISOString(),
                                           reason: "manual window" });
                        }}
                        className="bg-blue-600 hover:bg-blue-500 text-white">
                  {request.isPending ? "Requesting…" : "Request this window"}</Button>
              </div>
            </div>
          )}
          <div className="tg-card divide-y divide-[#26334d]" data-testid="cctv-history-list">
            {(history.data || []).map((h: any) => (
              <div key={h.journeyKey} className="flex items-center gap-3 px-4 py-2 text-xs">
                <Clapperboard className="h-4 w-4 text-slate-400" />
                <span className="font-mono text-slate-200">{fmtShort(h.startTime)} → {fmtShort(h.endTime)}</span>
                <span className="text-slate-500">{fmtMin(h.durationSec)} · {h.channels.join(" / ")}</span>
                <span className="font-mono text-[10px] text-amber-400 ml-auto">{h.availability.replace(/_/g, " ")}</span>
                {h.clipId ? <Button size="sm" variant="ghost" data-testid={`history-open-clip-${h.journeyId}`} onClick={() => openClip(h.clipId)} className="text-blue-300 h-7">Open clip</Button>
                  : can("reconciliation:decide") && <Button size="sm" variant="outline" data-testid={`history-request-clip-${h.journeyId}`} disabled={request.isPending} onClick={() => request.mutate({ vehicleId, channel, startTime: h.startTime, endTime: h.endTime, journeyKey: h.journeyKey, reason: "history" })} className="h-7 border-[#26334d] bg-transparent text-slate-200">Request clip</Button>}
              </div>
            ))}
            {vehicleId && !history.data?.length && <EmptyState text="No recordings in window" />}
          </div>
        </TabsContent>
        <TabsContent value="media" className="mt-4">
          <div className="flex gap-2 mb-3">
            <select data-testid="cctv-media-vehicle-select" className="h-9 rounded-md bg-[#0f141f] border border-[#26334d] text-sm px-3 text-slate-100"
                    value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              {(vehicles.data || []).map((v: any) => <option key={v.id} value={v.id}>{v.plate}</option>)}
            </select>
            <Button size="sm" variant="outline" data-testid="cctv-media-refresh" onClick={() => media.refetch()}
                    className="border-[#26334d] bg-transparent text-slate-200">
              <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Photos and video the unit itself has uploaded, straight from the camera platform. This is where
            a requested fragment lands.
          </p>
          <div className="tg-card divide-y divide-[#26334d]" data-testid="cctv-media-list">
            {media.isLoading ? <Spinner /> : media.data?.error || media.data?.detail
              ? <EmptyState text={media.data.error ? `${media.data.detail} ${media.data.error}` : media.data.detail} testId="media-empty" />
              : !media.data?.files?.length
              ? <EmptyState text="Nothing uploaded by this vehicle in the last 7 days" testId="media-empty" />
              : media.data.files.map((m: any, i: number) => (
                <div key={m.fileUrl || i} data-testid={`media-row-${i}`} className="flex items-center gap-3 px-4 py-2 text-xs">
                  <FileVideo className="h-4 w-4 text-purple-400 shrink-0" />
                  <span className="font-mono text-slate-100 w-16">{m.kind}</span>
                  <span className="font-mono text-slate-300 w-20">{m.channel}</span>
                  <span className="font-mono text-slate-400">{m.createdAt ? fmtDateTime(m.createdAt) : "—"}</span>
                  <span className="text-slate-500 ml-auto">{m.sizeBytes ? `${Math.round(m.sizeBytes / 1024)} KB` : ""}</span>
                  <a href={m.fileUrl} target="_blank" rel="noreferrer" data-testid={`media-open-${i}`}
                     className="text-blue-300 inline-flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" />Open</a>
                </div>
              ))}
          </div>
        </TabsContent>
        <TabsContent value="evidence" className="mt-4">
          <div className="tg-card divide-y divide-[#26334d]" data-testid="cctv-evidence-list">
            {!clips.data?.length && <EmptyState text="No evidence clips yet — request one from Trip Detail or History" testId="evidence-empty" />}
            {(clips.data || []).map((c: any) => (
              <div key={c.id} data-testid={`clip-row-${c.id}`} className="flex items-center gap-3 px-4 py-2 text-xs">
                <span className="font-mono text-slate-100 w-24">{c.plate}</span>
                <span className="font-mono text-slate-300">{fmtShort(c.startTime)} → {fmtShort(c.endTime)}</span>
                <span className="text-slate-500">{c.channel} · by {c.requestedBy}</span>
                <span data-testid={`clip-status-${c.id}`}
                      className={cn("font-mono text-[10px] rounded px-1.5 py-0.5 border",
                        c.status === "READY" ? "text-emerald-400 border-emerald-500/30"
                        : c.status === "REQUESTED" ? "text-cyan-400 border-cyan-500/30"
                        : c.status === "FAILED" ? "text-rose-400 border-rose-500/30"
                        : "text-amber-400 border-amber-500/30")}
                      title={c.detail || ""}>
                  {c.status}{c.sizeBytes ? ` · ${Math.round(c.sizeBytes / 1024)} KB` : ""}
                </span>
                <span className="font-mono text-[10px] text-slate-500 ml-auto">expires {fmtDateTime(c.expiresAt)} · {c.accessCount} access</span>
                {c.status !== "READY" && c.status !== "SIMULATED" && (
                  <Button size="sm" variant="ghost" data-testid={`clip-check-${c.id}`} disabled={checkUpload.isPending}
                          onClick={() => checkUpload.mutate(c.id)} className="text-cyan-300 h-7">
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />Check upload</Button>
                )}
                <Button size="sm" variant="ghost" data-testid={`clip-open-${c.id}`} onClick={() => openClip(c.id)} className="text-blue-300 h-7">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />{c.fileUrl ? "Open recording" : "Placeholder"}</Button>
                {can("reconciliation:decide") && <Button size="icon" variant="ghost" data-testid={`clip-delete-${c.id}`} onClick={() => del.mutate(c.id)} className="h-7 w-7 text-slate-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
